import { formatWeight, formatWeightChange, weightNumber } from '@tahi/core';
import { Link } from 'react-router';
import type { TileSize } from '../../../tiles/registry';
import { GoalBar } from '../components/GoalBar';
import { TrendChart } from '../components/TrendChart';
import { copy } from '../copy';
import { dayLabel, rateText } from '../format';
import { useWeightOverview, WEIGHT_BASE } from '../hooks';
import { FOCUS } from '../styles';

const LINK = `inline-flex min-h-11 items-center text-primary ${FOCUS}`;

/** "Weight": the 7-day average (or the latest), with its own Log button. */
export function CurrentWeightTile({ size }: { size: TileSize; config: unknown }) {
  const o = useWeightOverview();
  if (!o) return <p className="text-sm text-muted">{copy.loading}</p>;
  const hero = o.avg7 ?? o.latest?.valueG ?? null;
  const logLabel = o.todayEntry ? copy.editWeight : copy.logWeight;
  if (hero === null) {
    return (
      <Link to={`${WEIGHT_BASE}/log`} className={LINK}>
        {copy.logWeight}
      </Link>
    );
  }
  return (
    <div
      role="group"
      aria-label={`${o.avg7 !== null ? copy.average7 : copy.latest}, ${formatWeight(hero, o.unit)}`}
      className="flex flex-col gap-1"
    >
      <Link to={WEIGHT_BASE} className={`min-w-0 ${FOCUS}`}>
        <span className="font-mono text-[32px] leading-tight tabular-nums text-fg">
          {weightNumber(hero, o.unit)}
        </span>
        <span className="ml-1 text-base text-muted">{o.unit}</span>
      </Link>
      <p className="text-sm text-muted">
        {o.avg7 !== null ? copy.average7 : dayLabel(o.latest!.date, o.today)}
        {size === 'wide' && o.avgChange !== null
          ? ` · ${copy.avgChange(formatWeightChange(o.avgChange, o.unit))}`
          : ''}
      </p>
      <Link to={`${WEIGHT_BASE}/log`} className={`${LINK} text-sm`}>
        {logLabel}
      </Link>
    </div>
  );
}

/** "Weight trend": the last 30 days, compact. */
export function WeightTrendTile() {
  const o = useWeightOverview();
  if (!o) return <p className="text-sm text-muted">{copy.loading}</p>;
  return (
    <Link to={WEIGHT_BASE} className={`flex flex-col gap-1 ${FOCUS}`}>
      <TrendChart
        compact
        points={o.trend}
        unit={o.unit}
        goalG={o.settings.goalG}
        label={copy.trendLabel(
          o.trend.filter((p) => p.valueG !== null).length,
          o.avg7 !== null ? formatWeight(o.avg7, o.unit) : null,
        )}
      />
      <span className="text-sm text-muted">{rateText(o.rate, o.unit)}</span>
    </Link>
  );
}

/** "Weight goal": progress, or a quiet link to set one. */
export function WeightGoalTile() {
  const o = useWeightOverview();
  if (!o) return <p className="text-sm text-muted">{copy.loading}</p>;
  if (!o.goal) {
    return (
      <Link to={`${WEIGHT_BASE}/goal`} className={LINK}>
        {copy.setGoal}
      </Link>
    );
  }
  return <GoalBar goal={o.goal} unit={o.unit} />;
}
